import { BaseDragger } from "./BaseDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import { DraggerType, type Point2D, type Point3D } from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntitiesCommand } from "../commands/AddEntitiesCommand";
import { ReplaceWithRecognizedShapeCommand } from "../commands/ReplaceWithRecognizedShapeCommand";
import { simplifyPolyline } from "../algorithms/smooth";
import {
  recognizeShape,
  type RecognizedShape,
} from "../algorithms/shapeRecognition";
import { PREVIEW_LINE_STYLE, PREVIEW_WILL_CLOSE_STYLE } from "./previewStyles";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";
import { buildShapeEntity, isClosedShape } from "./recognizedShape";

/** Minimum distance in pixels between collected points */
const MIN_POINT_DISTANCE = 2;

/**
 * Dragger for freehand/pen drawing.
 * Collects points during continuous drag and creates OdDbPolyline on end.
 * With shape recognition enabled, a stroke matching a regular shape (line,
 * circle, ellipse, triangle, rectangle, quadrilateral) is replaced by that
 * shape when the pen is lifted; the first undo turns it back into the
 * freehand stroke.
 */
export class FreehandDragger extends BaseDragger {
  readonly type = DraggerType.Freehand;

  private lastScreenPoint: Point2D | null = null;
  private previewId: string | null = null;
  private screenPoints: Point2D[] = [];
  private closeIndicatorId: string | null = null;

  // ============== Dragger Callbacks ==============

  protected onStart(screen: Point2D, world: Point3D): void {
    // Initialize points array with first point
    this.points = [world];
    this.screenPoints = [screen];
    this.lastScreenPoint = screen;

    // Create preview overlay
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      this.previewId = overlay.createPolyline([screen], PREVIEW_LINE_STYLE);
    }
  }

  protected onDrag(screen: Point2D, world: Point3D): void {
    // Filter points by minimum distance to reduce noise
    if (this.lastScreenPoint) {
      const dx = screen.x - this.lastScreenPoint.x;
      const dy = screen.y - this.lastScreenPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < MIN_POINT_DISTANCE) {
        return;
      }
    }

    // Add point to collection
    this.points.push(world);
    this.screenPoints.push(screen);
    this.lastScreenPoint = screen;

    // Update preview overlay
    const overlay = this.viewer.overlayManager;
    if (overlay && this.previewId) {
      overlay.addPointToPolyline(this.previewId, screen);
    }

    // Update auto-close indicator
    this.updateCloseIndicator(screen);
  }

  protected onEnd(): void {
    // Clear preview first
    this.clearPreview();

    // Need at least 2 points for a polyline
    if (this.points.length < 2) {
      this.clear();
      return;
    }

    // Check if we should auto-close the polyline
    const shouldClose = this.shouldAutoClose();

    // Shape recognition runs on the raw stroke, before smoothing
    const recognizedShape = this.viewer.shapeRecognition
      ? recognizeShape(this.screenPoints)
      : null;

    // Apply smoothening if enabled. It only shapes the freehand stroke: a
    // recognized shape is built from the raw stroke above
    if (this.viewer.smoothen > 0 && this.points.length > 2) {
      const points3D = this.points.map(
        (p) => [p.x, p.y, p.z] as [number, number, number],
      );
      const simplified = simplifyPolyline(
        points3D,
        this.viewer.smoothen,
        shouldClose, // closeContour - true if auto-closing
        (d) => this.viewer.screenDistanceToWorld(d),
      );
      this.points = simplified.map(([x, y, z]) => ({ x, y, z }));
    }

    // Create the freehand polyline. A recognized stroke is committed too, then
    // replaced by its shape in a second command: the first undo brings the
    // freehand stroke back, the second removes it.
    const entityInfo = this.createPolyline(shouldClose);

    if (entityInfo) {
      const freehandIds = this.styleAndHatch(entityInfo.id, shouldClose);
      this.viewer.executeCommand(
        new AddEntitiesCommand(this.viewer, {
          entityHandles: freehandIds.map((id) => getEntityHandle(id)),
          originalLayerName: this.layerName,
        }),
      );

      const shapeIds = recognizedShape
        ? this.replaceWithShape(recognizedShape, freehandIds)
        : [];

      trackShape(
        "freehand",
        recognizedShape && shapeIds.length > 0
          ? {
              recognized:
                recognizedShape.kind === "polygon"
                  ? recognizedShape.type
                  : recognizedShape.kind,
            }
          : undefined,
      );

      // Auto-select if enabled
      this.selectCreatedEntity(shapeIds[0] ?? entityInfo.id);
      for (const id of [...freehandIds, ...shapeIds]) {
        deleteObj(id);
      }
    }

    // Update display
    this.viewer.update("FreehandDragger.onEnd");

    // Clear state for next stroke
    this.clear();
  }

  /** Layer of the active NoteConfig, where new entities are drawn */
  private get layerName(): string {
    return this.viewer.activeNoteConfig.props.layername ?? "0";
  }

  /**
   * Apply the NoteConfig styling to a freshly created entity and add its
   * hatch if it is closed and the NoteConfig has one.
   * @returns The entity ID followed by the hatch ID, if any
   */
  private styleAndHatch(
    entityId: DrawingModule.OdDbObjectId,
    closed: boolean,
  ): DrawingModule.OdDbObjectId[] {
    this.applyNoteConfigToEntity(entityId);

    const entityIds: DrawingModule.OdDbObjectId[] = [entityId];
    if (closed) {
      const hatchId = this.createHatchForEntity(entityId);
      if (hatchId) {
        entityIds.push(hatchId);
      }
    }
    return entityIds;
  }

  /**
   * Build the recognized shape and swap it in for the freehand stroke just
   * committed, as one undoable command.
   * @returns The shape entity ID followed by its hatch ID, or [] on failure
   */
  private replaceWithShape(
    shape: RecognizedShape,
    freehandIds: DrawingModule.OdDbObjectId[],
  ): DrawingModule.OdDbObjectId[] {
    const shapeInfo = buildShapeEntity(this.viewer, shape, (screen) =>
      this.viewer.screenToWorld(screen),
    );
    if (!shapeInfo) return [];

    const shapeIds = this.styleAndHatch(shapeInfo.id, isClosedShape(shape));
    this.viewer.executeCommand(
      new ReplaceWithRecognizedShapeCommand(this.viewer, {
        freehandIds,
        shapeIds,
        originalLayerName: this.layerName,
      }),
    );
    return shapeIds;
  }

  protected onCancel(): void {
    // Clear preview and state without creating entity
    this.clearPreview();
    this.clear();
    this.viewer.update("FreehandDragger.onCancel");
  }

  private clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      if (this.previewId) {
        overlay.remove(this.previewId);
        this.previewId = null;
      }
      if (this.closeIndicatorId) {
        overlay.remove(this.closeIndicatorId);
        this.closeIndicatorId = null;
      }
    }
  }

  // ============== Auto-Close Detection ==============

  /**
   * Check if the polyline should be auto-closed.
   * Returns true if auto-close is enabled and the end point is close enough to the start point.
   */
  private shouldAutoClose(): boolean {
    // Check if auto-close is enabled
    if (!this.viewer.autoClose) {
      return false;
    }

    // Need at least 3 points to make a meaningful closed shape
    if (this.screenPoints.length < 3) {
      return false;
    }

    // Get start and end screen points
    const startScreen = this.screenPoints[0];
    const endScreen = this.screenPoints[this.screenPoints.length - 1];

    // Calculate distance in screen pixels
    const dx = endScreen.x - startScreen.x;
    const dy = endScreen.y - startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Compare against threshold
    return distance <= this.viewer.autoCloseThreshold;
  }

  /**
   * Check if current position is within auto-close threshold.
   */
  private isWithinCloseThreshold(screen: Point2D): boolean {
    if (!this.viewer.autoClose || this.screenPoints.length < 3) {
      return false;
    }

    const startScreen = this.screenPoints[0];
    const dx = screen.x - startScreen.x;
    const dy = screen.y - startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    return distance <= this.viewer.autoCloseThreshold;
  }

  /**
   * Update the auto-close indicator based on current position.
   * Shows a highlighted circle around the start point when close enough to auto-close.
   */
  private updateCloseIndicator(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const willClose = this.isWithinCloseThreshold(screen);

    if (willClose) {
      // Show or update the close indicator
      const startScreen = this.screenPoints[0];
      const indicatorRadius = this.viewer.autoCloseThreshold;

      if (this.closeIndicatorId) {
        // Update existing indicator position
        overlay.update(this.closeIndicatorId, {
          cx: startScreen.x,
          cy: startScreen.y,
        });
      } else {
        // Create new indicator
        this.closeIndicatorId = overlay.createCircle(
          startScreen.x,
          startScreen.y,
          indicatorRadius,
          PREVIEW_WILL_CLOSE_STYLE,
        );
      }
    } else {
      // Hide the close indicator when not close enough
      if (this.closeIndicatorId) {
        overlay.remove(this.closeIndicatorId);
        this.closeIndicatorId = null;
      }
    }
  }

  // ============== Polyline Creation ==============

  /**
   * Create OdDbPolyline from collected points.
   * @param closed Whether to close the polyline (auto-close detected)
   * @returns Entity info with object ID, or null on failure
   */
  private createPolyline(
    closed: boolean = false,
  ): { id: DrawingModule.OdDbObjectId } | null {
    const module = this.module;
    const db = this.viewer.database;

    if (!db || this.points.length < 2) {
      return null;
    }

    // Create polyline object
    const polyline = module.OdDbPolyline.createObject();
    polyline.setDatabaseDefaults(db, true);

    // Add all vertices
    for (let i = 0; i < this.points.length; i++) {
      const point = this.points[i];
      const point2d = new module.OdGePoint2d(point.x, point.y);

      // addVertexAt(index, point2d, bulge, startWidth, endWidth, vertexId)
      // bulge = 0 for straight segments
      polyline.addVertexAt(i, point2d, 0, 0, 0, 0);

      deleteObj(point2d);
    }

    // Set closed if auto-close detected
    if (closed) {
      polyline.setClosed(true);
    }

    // Append to current space
    const currentSpaceId = getCurrentSpaceId(db);
    const currentSpace = openAs(
      currentSpaceId,
      module.OdDbBlockTableRecord,
      OpenMode.kForWrite,
    );
    const entityId = currentSpace.appendOdDbEntity(polyline);
    polyline.recordGraphicsModified(true);
    noteGraphicsModified("Polyline");

    deleteObj(currentSpace);
    deleteObj(polyline);

    return { id: entityId };
  }

  // ============== Cleanup ==============

  protected clear(): void {
    super.clear();
    this.lastScreenPoint = null;
    this.screenPoints = [];
  }
}
